'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as React from 'react'

import { cn } from '../../utils/cn'

function TooltipProvider({
  delayDuration = 300,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 8,
  title,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & { title?: string }) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-secondary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 text-2xs px-sm py-xs [&:has([data-slot])]:p-sm [&>p+p]:mt-xs z-50 w-fit max-w-80 origin-(--radix-tooltip-content-transform-origin) rounded-md border text-pretty shadow',
          className
        )}
        {...props}
      >
        {title && (
          <p data-slot="tooltip-title" className="text-2xs text-foreground mb-1 font-normal">
            {title}
          </p>
        )}
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

function TooltipItem({
  label,
  value,
  className,
  variant = 'default'
}: {
  label: React.ReactNode
  value: React.ReactNode
  className?: string
  variant?: 'default' | 'secondary'
}) {
  return (
    <div
      data-slot="tooltip-item"
      className={cn('text-2xs mt-xs flex items-center justify-between gap-6 first:mt-0', className)}
    >
      <span className="text-secondary-foreground whitespace-nowrap">{label}</span>
      <span
        className={cn('whitespace-nowrap', {
          'text-foreground': variant === 'default',
          'text-secondary-foreground': variant === 'secondary'
        })}
      >
        {value}
      </span>
    </div>
  )
}

function TooltipSeparator({ className }: { className?: string }) {
  return (
    <div
      data-slot="tooltip-separator"
      className={cn('bg-secondary my-sm h-[0.5px] last:mb-0', className)}
    />
  )
}

function TooltipLink({ children, className, ...props }: React.ComponentProps<'a'>) {
  return (
    <div data-slot="tooltip-link" className="mt-xs first:mt-0">
      <a className={cn('text-primary hover:underline', className)} {...props}>
        {children}
      </a>
    </div>
  )
}

export {
  Tooltip,
  TooltipContent,
  TooltipItem,
  TooltipLink,
  TooltipProvider,
  TooltipSeparator,
  TooltipTrigger
}
